import { ChatConversations, Users } from '@prisma/client';
import axios from './api';
import API_URL from '../apiconfig';
import {
  ChatFilter,
  ConversationsPage,
  UserConversation,
} from '../types/chats';
import client from '../utils/react-query/client';
import { Appointment } from '../interfaces';

export async function getConversations({
  businessId,
  limit = 30,
  offsetTs,
  filter,
}: {
  businessId: string;
  limit?: number;
  offsetTs?: number;
  filter?: ChatFilter;
}) {
  const response = await axios.get(`${API_URL}/v1/talkjs/conversations`, {
    params: {
      businessId,
      asEmployee: true,
      limit,
      offsetTs,
      filter,
    },
  });

  return response.data as ConversationsPage;
}

export async function getConversation(conversationId: string) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/talkjs/conversations/${conversationId}`,
    );

    return response.data.result as UserConversation;
  } catch (error) {
    throw new Error('Cannot get conversation', { cause: error });
  }
}

export async function getInternalConversation(conversationId: string) {
  const response = await axios.get(
    `${API_URL}/v1/talkjs/conversations/${conversationId}/internal`,
  );

  return response.data.result as ChatConversations | null;
}

export async function getConversationAppointment(
  conversationId: string,
  appointmentId: string,
) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/talkjs/conversations/${conversationId}/appointments/${appointmentId}`,
    );

    return response.data.result as Appointment;
  } catch (error) {
    throw new Error('Cannot get conversation', { cause: error });
  }
}

export async function markConversationAsRead(
  conversationId: string,
  isUnread: boolean = false,
) {
  try {
    await axios.patch(
      `${API_URL}/v1/talkjs/conversations/${conversationId}/read`,
      {
        isUnread,
      },
    );

    return true;
  } catch (error) {
    throw new Error('Cannot mark conversation as read', { cause: error });
  }
}

export async function muteConversation(conversationId: string, muted: boolean) {
  try {
    await axios.patch(
      `${API_URL}/v1/talkjs/conversations/${conversationId}/mute`,
      {
        muted,
      },
    );

    return true;
  } catch (error) {
    throw new Error('Cannot mute conversation', { cause: error });
  }
}

export async function archiveConversation(
  conversationId: string,
  archived: boolean,
) {
  try {
    await axios.patch(
      `${API_URL}/v1/talkjs/conversations/${conversationId}/archive`,
      {
        archived,
      },
    );

    return true;
  } catch (error) {
    throw new Error('Cannot archive conversation', { cause: error });
  }
}

export async function markConversationImportant(
  conversationId: string,
  important: boolean,
) {
  try {
    await axios.patch(
      `${API_URL}/v1/talkjs/conversations/${conversationId}/important`,
      {
        important,
      },
    );

    return true;
  } catch (error) {
    throw new Error('Cannot mark conversation as important', { cause: error });
  }
}

export async function resetConversations(filter?: ChatFilter) {
  await client.resetQueries({
    queryKey: ['chat-conversations', ...(filter ? [{ filter }] : [])],
    exact: false,
  });
}

export async function invalidateConversations(filter?: ChatFilter) {
  await client.invalidateQueries({
    queryKey: ['chat-conversations', ...(filter ? [{ filter }] : [])],
    exact: false,
  });
}

export async function searchChatsByCustomer({
  customerIds,
  businessId,
}: {
  customerIds: string[];
  businessId: string;
}) {
  const response = await axios.get(
    `${API_URL}/v1/talkjs/conversations/search`,
    {
      params: {
        customerIds,
        businessId,
      },
    },
  );

  return response.data.result as (ChatConversations & {
    customer: {
      user: Pick<Users, 'firstName' | 'lastName'>;
    };
  })[];
}

export async function updateChatParticipants(conversationId: string) {
  const response = await axios.patch(
    `${API_URL}/v1/talkjs/conversations/${conversationId}/participants`,
  );

  return response.data.result as { id: string };
}

export async function leaveConversation(conversationId: string) {
  const response = await axios.post(
    `${API_URL}/v1/talkjs/conversations/${conversationId}/leave`,
  );

  return response.data.result as boolean;
}

export async function addParticipant(conversationId: string, userId: string) {
  const response = await axios.post(
    `${API_URL}/v1/talkjs/conversations/${conversationId}/participants`,
    {
      userId,
    },
  );

  return response.data.result as boolean;
}
